import { memo, useCallback, useMemo, useState } from 'react';

import { ChevronDownIcon, FileTypeCornerIcon } from '@air/design-icons';
import { Button } from '@air/primitive-button';
import { Popover } from '@air/primitive-popover';
import { cn } from '@air/utils-tailwind';

import { ShareDesignFontRowBrandOption } from '~/components/ShareModal/components/ShareDesignFontRowBrandOption';
import { ShareModalRow } from '~/components/ShareModal/components/ShareModalRow';
import { type BrandFontEntry } from '~/components/ShareModal/shareLinkDesignResolution';
import { UpsellFlashIconButton } from '~/components/SharePrivateContentModal/shared/UpsellFlashIconButton';
import { BuiltInFontVariantSelect } from '~/components/v2/components/shared/fonts/BuiltInFontVariantSelect';
import { SearchableBuiltInFontSelect } from '~/components/v2/components/shared/fonts/SearchableBuiltInFontSelect';
import { SHARE_MODAL_UPSELL_MESSAGE } from '~/constants/upsells';

/** Family the viewer renders when the share link has no stored custom font. Mirrors the prototype. */
const DEFAULT_FONT_FAMILY = 'Inter';

export type ShareDesignFontRowProps = {
  /** data-testid for the row container. */
  'data-testid'?: string;
  /** Brand-kit font entries listed (grouped by their source field) above the built-in selects. */
  brandFonts?: BrandFontEntry[];
  disabled: boolean;
  /**
   * Plan-gate-only upsell affordance — kept separate from `disabled` (which also covers in-flight
   * saves) so the flash icon never flashes during saves.
   */
  showUpsell: boolean;
  label: string;
  /** Currently selected built-in font family, if any. */
  fontFamily?: string;
  /** The brand value id backing the current font, for the brand options' selected state. */
  selectedBrandFontId?: string;
  /** Currently selected variant for the family, if any. */
  variant?: string;
  /**
   * Called with the picked family + variant whenever either selector changes. Picking a family with
   * no variant yet emits an undefined variant so the parent can commit and let the server default it.
   */
  onChange: (next: { fontFamily: string; variant?: string }) => void;
  /** Called when the user clears the current font (commits `null` upstream). */
  onClear: () => void;
  /**
   * Called when the dropdown opens or closes. Optional accessory: a consumer that commits a family
   * pick before its variant lands (see `onChange`) can use the close to flush that pending pick;
   * consumers whose commit doesn't need flushing simply don't pass it.
   */
  onOpenChange?: (isOpen: boolean) => void;
  /** Called with a picked brand font entry (commits a `contextSource` ref upstream). */
  onSelectBrandFont?: (brandFont: BrandFontEntry) => void;
};

/**
 * Design-tab row for the public viewer's headline/body font. Composes the built-in (`system`)
 * family/variant selects used by Context Sources, plus quick-pick brand-kit (`contextSource`) font
 * entries when a brand context is selected — all tucked behind a single inline dropdown trigger
 * that mirrors the color rows.
 *
 * TODO(COR-1951): asset-font upload is out of scope (no font-upload UI yet).
 */
export const ShareDesignFontRow = memo(
  ({
    'data-testid': dataTestId,
    brandFonts = [],
    disabled,
    showUpsell,
    label,
    fontFamily,
    selectedBrandFontId,
    variant,
    onChange,
    onClear,
    onOpenChange,
    onSelectBrandFont,
  }: ShareDesignFontRowProps) => {
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);

    const handleOpenChange = useCallback(
      (isOpen: boolean) => {
        setIsPopoverOpen(isOpen);
        onOpenChange?.(isOpen);
      },
      [onOpenChange],
    );

    const isCustomFont = !!fontFamily;
    const displayFamily = fontFamily ?? DEFAULT_FONT_FAMILY;
    /**
     * The brand entry backing the current font, if its kit is loaded. Purely display: the trigger
     * shows the entry's label (the nice brand name) instead of the file-derived family. When the
     * stored ref's kit isn't selected this session, this misses and the trigger falls back to the
     * built-in "Custom" + resolved family display.
     */
    const selectedBrandFont = brandFonts.find((brandFont) => brandFont.id === selectedBrandFontId);
    const builtInFontName = isCustomFont ? displayFamily : 'Default';
    const triggerFontName = selectedBrandFont?.label ?? builtInFontName;

    const handleFamilyChange = useCallback(
      (nextFamily: string) => {
        // Changing the family invalidates the prior variant; let the server/variant select re-default.
        onChange({ fontFamily: nextFamily });
      },
      [onChange],
    );

    const handleVariantChange = useCallback(
      (nextVariant: string) => {
        if (!fontFamily) {
          return;
        }

        onChange({ fontFamily, variant: nextVariant });
      },
      [fontFamily, onChange],
    );

    const handleReset = useCallback(() => {
      onClear();
      handleOpenChange(false);
    }, [handleOpenChange, onClear]);

    const handleSelectBrandFont = useCallback(
      (brandFont: BrandFontEntry) => {
        // Re-picking the already-selected brand font is a no-op: the draft already holds this font's
        // server-resolved ref, and re-committing the bare id-only write shape would overwrite those
        // resolved fields under wholesale-replace — degrading the preview and dirtying the editor
        // while the selection looks unchanged. Clearing is a separate action ("Reset to default").
        if (brandFont.id === selectedBrandFontId) {
          handleOpenChange(false);
          return;
        }

        onSelectBrandFont?.(brandFont);
        handleOpenChange(false);
      },
      [handleOpenChange, onSelectBrandFont, selectedBrandFontId],
    );

    // Group brand fonts by their source field so the popover renders one labelled section per brand
    // field (e.g. "TYPOGRAPHY") instead of a flat list — mirrors the prototype's grouping.
    const brandFontGroups = useMemo(() => {
      const groups: { fieldName: string; fonts: BrandFontEntry[] }[] = [];

      for (const brandFont of brandFonts) {
        const group = groups.find((candidate) => candidate.fieldName === brandFont.fieldName);

        if (group) {
          group.fonts.push(brandFont);
        } else {
          groups.push({ fieldName: brandFont.fieldName, fonts: [brandFont] });
        }
      }

      return groups;
    }, [brandFonts]);

    return (
      <ShareModalRow data-testid={dataTestId} label={label}>
        {showUpsell && <UpsellFlashIconButton>{SHARE_MODAL_UPSELL_MESSAGE}</UpsellFlashIconButton>}
        <Popover
          align="end"
          className="w-[280px]"
          onOpenChange={handleOpenChange}
          open={isPopoverOpen}
          size="large"
          trigger={
            <button
              aria-expanded={isPopoverOpen}
              aria-haspopup="dialog"
              aria-label={`${label}, ${triggerFontName}`}
              className="flex h-8 w-[210px] items-center justify-between gap-1.5 rounded-6 border ads:border-input border-gray-5 ads:bg-input bg-gray-1 px-2 ads:hover:border-hover-input hover:border-gray-7 disabled:cursor-not-allowed disabled:opacity-50 ads:disabled:hover:border-input disabled:hover:border-gray-5"
              data-testid="SHARE_DESIGN_FONT_TRIGGER"
              disabled={disabled}
              type="button"
            >
              <span className="flex min-w-0 flex-1 items-center gap-1.5">
                <FileTypeCornerIcon className="shrink-0 ads:text-metadata text-gray-9" size="xs" />
                {selectedBrandFont ? (
                  <span className="min-w-0 truncate ads:text-primary text-12 text-gray-12">
                    {selectedBrandFont.label}
                  </span>
                ) : (
                  <>
                    <span
                      className={cn(
                        'text-12',
                        isCustomFont ? 'ads:text-primary text-gray-12' : 'ads:text-metadata text-gray-9',
                      )}
                    >
                      {isCustomFont ? 'Custom' : 'Default'}
                    </span>
                    <span className="min-w-0 truncate font-mono ads:text-metadata text-10 text-gray-8">
                      {displayFamily}
                    </span>
                  </>
                )}
              </span>
              <ChevronDownIcon size="xs" className="shrink-0 ads:text-metadata text-gray-9" />
            </button>
          }
        >
          <div className="flex flex-col gap-2" data-testid="SHARE_DESIGN_FONT_CONTROLS">
            {brandFontGroups.length > 0 && (
              <div className="flex flex-col gap-1" data-testid="SHARE_DESIGN_FONT_BRAND_OPTIONS">
                {brandFontGroups.map((group) => (
                  <div className="flex flex-col" key={group.fieldName}>
                    <div className="px-1 pt-1 pb-0.5 font-semibold ads:text-metadata text-10 text-gray-9 uppercase tracking-wide">
                      {group.fieldName}
                    </div>
                    {group.fonts.map((brandFont) => (
                      <ShareDesignFontRowBrandOption
                        brandFont={brandFont}
                        isSelected={brandFont.id === selectedBrandFontId}
                        key={brandFont.id}
                        onSelect={handleSelectBrandFont}
                      />
                    ))}
                  </div>
                ))}
                <hr className="h-px border-0 ads:bg-ads-gray-4 bg-gray-4" />
              </div>
            )}
            <SearchableBuiltInFontSelect
              onClear={isCustomFont ? handleReset : undefined}
              onValueChange={handleFamilyChange}
              value={fontFamily}
            />
            <BuiltInFontVariantSelect family={fontFamily ?? ''} onValueChange={handleVariantChange} value={variant} />
            {isCustomFont && (
              <Button
                appearance="ghost"
                color="gray"
                data-testid="SHARE_DESIGN_FONT_CLEAR"
                onClick={handleReset}
                size="small"
                type="button"
              >
                Reset to default
              </Button>
            )}
          </div>
        </Popover>
      </ShareModalRow>
    );
  },
);

ShareDesignFontRow.displayName = 'ShareDesignFontRow';
